import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { updateHomeProfileDetails } from "../../../HttpRequest/afterlogin";
import defaultImage from "../../../../assets/img/profiles/icons8-test-account-90.png";

const AdminProfileView = ({ profileData, userProfileUrl, onUpdateProfile, onPhotoUpload }) => {
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [adminData, setAdminData] = useState({
    id: profileData?.id || "",
    name: (profileData?.firstName || profileData?.lastName) ? `${profileData?.firstName || ''} ${profileData?.lastName || ''}`.trim() : (profileData?.email || ""),
    email: profileData?.email || "",
    phone: profileData?.phoneNumber || profileData?.mobileNumber || "",
    adminId: profileData?.id ? `ADM-${profileData.id}` : "",
    designation: profileData?.designation || profileData?.role || "Administrator",
    department: profileData?.department || "Operations & Admissions",
    organization: profileData?.organizationName || profileData?.organization || "",
    status: profileData?.status !== false ? "Active" : "Inactive",
    joiningDate: profileData?.createdDate ? String(profileData.createdDate).substring(0, 10) : "",
    officeLocation: profileData?.city ? `${profileData.city}, ${profileData.country || ''}` : "",
    reportingManager: profileData?.reportingManager || ""
  });

  useEffect(() => {
    if (profileData) {
      setAdminData({
        id: profileData?.id || "",
        name: (profileData?.firstName || profileData?.lastName) ? `${profileData?.firstName || ''} ${profileData?.lastName || ''}`.trim() : (profileData?.email || ""),
        email: profileData?.email || "",
        phone: profileData?.phoneNumber || profileData?.mobileNumber || "",
        adminId: profileData?.id ? `ADM-${profileData.id}` : "",
        designation: profileData?.designation || profileData?.role || "Administrator",
        department: profileData?.department || "Operations & Admissions",
        organization: profileData?.organizationName || profileData?.organization || "",
        status: profileData?.status !== false ? "Active" : "Inactive",
        joiningDate: profileData?.createdDate ? String(profileData.createdDate).substring(0, 10) : "",
        officeLocation: profileData?.city ? `${profileData.city}, ${profileData.country || ''}` : "",
        reportingManager: profileData?.reportingManager || ""
      });
    }
  }, [profileData]);

  const val = (v) => (v && String(v).trim() !== "" ? v : "-");

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (adminData.id) {
        await updateHomeProfileDetails({
          id: adminData.id,
          firstName: adminData.name.split(" ")[0] || adminData.name,
          lastName: adminData.name.split(" ").slice(1).join(" ") || "",
          email: adminData.email,
          phoneNumber: adminData.phone
        });
      }
      setIsEditing(false);
      if (onUpdateProfile) {
        onUpdateProfile(adminData);
      }
      Swal.fire({
        icon: "success",
        title: "Admin Profile Updated",
        text: "Your administrative profile information has been saved successfully.",
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error("Error updating admin profile:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: "Could not update profile details."
      });
    } finally {
      setSaving(false);
    }
  };

  const permissionsList = [
    { title: "Student Record Management", desc: "View & Edit Student Personal & Academic Details", level: "Full Access" },
    { title: "Application Review & Status", desc: "Approve, Reject, or Assign University Applications", level: "Full Access" },
    { title: "Document Verification", desc: "Verify Transcripts, LORs, SOPs, and Bank Statements", level: "Full Access" },
    { title: "Lead Allocation", desc: "Reassign Students and Leads across Counselor Queues", level: "Full Access" },
    { title: "Financial & Loan Approvals", desc: "Review Loan References & Discount Deals", level: "Granted" },
    { title: "Data Export & Reports", desc: "Generate CSV / Excel Operational Reports", level: "Full Access" }
  ];

  return (
    <div className="admin-profile-container">
      {/* Admin Hero Header */}
      <div className="admin-header-card">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-4 flex-wrap">
            <div
              className="profile-avatar-wrapper"
              style={{ cursor: "pointer" }}
              onClick={() => fileInputRef.current?.click()}
              title="Click to change profile picture"
            >
              <img
                src={userProfileUrl || defaultImage}
                alt="Profile Avatar"
                className="profile-avatar-img-large"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = defaultImage;
                }}
              />
              <div className="avatar-edit-overlay" title="Click to upload profile photo">
                <i className="fas fa-camera"></i>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/png, image/jpeg, image/jpg, image/webp"
                onChange={onPhotoUpload}
              />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="badge-role badge-admin">
                  <i className="fas fa-user-shield"></i> Operations Admin
                </span>
                <span className="badge-status-active">
                  <i className="fas fa-check-circle"></i> {adminData.status}
                </span>
              </div>
              <h2 className="m-0 text-white font-weight-bold">{val(adminData.name)}</h2>
              <p className="text-light opacity-75 mb-1 mt-1" style={{ fontSize: "0.95rem" }}>
                <i className="fas fa-building me-1"></i> {val(adminData.designation)} &bull; {val(adminData.department)}
              </p>
              <p className="text-light opacity-60 m-0" style={{ fontSize: "0.85rem" }}>
                Employee ID: <span className="fw-bold">{val(adminData.adminId)}</span> {adminData.organization ? `• ${adminData.organization}` : ""}
              </p>
            </div>
          </div>

          <div className="d-flex flex-column align-items-end gap-2">
            {!isEditing ? (
              <button
                className="btn btn-light btn-sm fw-bold px-3 py-2 rounded-pill shadow-sm"
                onClick={() => setIsEditing(true)}
              >
                <i className="fas fa-user-edit me-1"></i> Edit Profile
              </button>
            ) : (
              <button
                className="btn btn-outline-light btn-sm fw-bold px-3 py-2 rounded-pill"
                onClick={() => setIsEditing(false)}
              >
                <i className="fas fa-times me-1"></i> Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Admin Nav Tabs */}
      <div className="role-nav-tabs">
        <button
          className={`role-tab-btn ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          <i className="fas fa-id-card me-2"></i> Professional Overview
        </button>
        <button
          className={`role-tab-btn ${activeTab === "permissions" ? "active" : ""}`}
          onClick={() => setActiveTab("permissions")}
        >
          <i className="fas fa-shield-alt me-2"></i> Permissions & Scope
        </button>
      </div>

      {/* Tab Content */}
      {!isEditing ? (
        <div>
          {activeTab === "overview" && (
            <div className="role-card">
              <div className="role-card-header">
                <h4 className="role-card-title">
                  <i className="fas fa-briefcase"></i> Organization & Work Details
                </h4>
              </div>
              <div className="info-grid mb-4">
                <div className="info-item">
                  <div className="info-label">Full Name</div>
                  <div className="info-value">{val(adminData.name)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Employee / Admin ID</div>
                  <div className="info-value">{val(adminData.adminId)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Work Email</div>
                  <div className="info-value">{val(adminData.email)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Official Phone</div>
                  <div className="info-value">{val(adminData.phone)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Designation</div>
                  <div className="info-value">{val(adminData.designation)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Department</div>
                  <div className="info-value">{val(adminData.department)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Organization</div>
                  <div className="info-value">{val(adminData.organization)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Office Location</div>
                  <div className="info-value">{val(adminData.officeLocation)}</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "permissions" && (
            <div className="role-card">
              <div className="role-card-header">
                <h4 className="role-card-title">
                  <i className="fas fa-user-cog"></i> Role Permissions & Administrative Scope
                </h4>
              </div>
              <div className="row g-3">
                {permissionsList.map((perm, idx) => (
                  <div key={idx} className="col-md-6">
                    <div className="info-item d-flex align-items-start justify-content-between">
                      <div>
                        <h6 className="m-0 font-weight-bold" style={{ color: "#FFE082" }}>
                          <i className="fas fa-check-circle me-2" style={{ color: "#D4AF37" }}></i>{perm.title}
                        </h6>
                        <small className="text-muted d-block mt-1">{perm.desc}</small>
                      </div>
                      <span className="badge-role badge-admin">
                        {perm.level}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* EDIT ADMIN PROFILE FORM */
        <form onSubmit={handleSave} className="role-card">
          <div className="role-card-header">
            <h4 className="role-card-title">
              <i className="fas fa-user-edit"></i> Edit Administrative Profile
            </h4>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="profile-input-group">
                <label className="profile-label">Full Name</label>
                <input
                  type="text"
                  className="profile-input"
                  value={adminData.name}
                  onChange={(e) => setAdminData({ ...adminData, name: e.target.value })}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="profile-input-group">
                <label className="profile-label">Work Email</label>
                <input
                  type="email"
                  className="profile-input"
                  value={adminData.email}
                  onChange={(e) => setAdminData({ ...adminData, email: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="profile-input-group">
                <label className="profile-label">Official Phone</label>
                <input
                  type="text"
                  className="profile-input"
                  value={adminData.phone}
                  onChange={(e) => setAdminData({ ...adminData, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="profile-input-group">
                <label className="profile-label">Designation / Title</label>
                <input
                  type="text"
                  className="profile-input"
                  value={adminData.designation}
                  onChange={(e) => setAdminData({ ...adminData, designation: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top border-secondary">
            <button
              type="button"
              className="btn-role-secondary"
              onClick={() => setIsEditing(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn-role-primary" disabled={saving}>
              {saving ? "Saving..." : "Save Admin Profile"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AdminProfileView;
